import { getSharedStyleSheet } from './sharedStyleSheet.js';

class TokenInput extends HTMLElement {
    constructor() {
        super();
        this.obj = null;
    }

    static get boundAttributes() {
        return ['tokens', 'name', 'size', 'max-token-length'];
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        this[attrName] = newValue;
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: "open"});

            // Make sure element obeys parent sizing
            this.shadowRoot.host.style.display = 'inline-block';

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._wrapper = document.createElement('div');
            this._wrapper.className = 'token-input-wrapper';
            this.shadowRoot.appendChild(this._wrapper);

            this._ul = document.createElement('ul');
            this._wrapper.appendChild(this._ul);

            if (this.size === "small") {
                this._ul.classList.add('small');
            }

            // Select text box if the user clicks anywhere other than on a token
            this._ul.addEventListener('click', (e) => {
                if (e.target.tagName.toLowerCase() !== 'li' && !e.target.classList.contains('.delete')) {
                    this._inputText.focus();
                }
            })

            this._li_text = document.createElement('li');
            this._ul.appendChild(this._li_text);

            this._inputText = document.createElement('input');
            this._inputText.setAttribute('type', 'text');
            this._inputText.id = 'input_txt';
            this._inputText.className = 'no-style';
            this._inputText.setAttribute('placeholder', 'Enter tags separated by commas');
            this._li_text.appendChild(this._inputText);

            if (this.name) {
                this._hiddenText = document.createElement('input');
                this._hiddenText.setAttribute('type', 'hidden');
                this._hiddenText.name = this.name;
                this.append(this._hiddenText);
            }

            this._inputText.addEventListener('keyup', e => {
                this.parseTextInput((e.key === 'Enter'));
            });

            this._inputText.addEventListener('blur', e => {
                this.parseTextInput(true);
            });
        }

        this.render();
    }

    disconnectedCallback() {
        this.parseTextInput();
    }

    get tokens() {
        return this.getAttribute('tokens');
    }

    set tokens(_tokens) {
        this.setAttribute("tokens", _tokens);
        this.syncTokens();
    }

    get name() {
        return this.getAttribute('name');
    }

    set name(_name) {
        this.setAttribute("name", _name);
    }

    get size() {
        return this.getAttribute('size');
    }

    set size(_size) {
        this.setAttribute("size", _size);
    }

    // Tag columns differ in width per table (20 for projects and tasks, 50 for
    // files), so the limit is set by whoever places the element. No attribute
    // means no limit.
    get maxTokenLength() {
        const value = parseInt(this.getAttribute('max-token-length'), 10);
        return Number.isNaN(value) || value < 1 ? null : value;
    }

    set maxTokenLength(_length) {
        this.setAttribute("max-token-length", _length);
    }

    tokensAsArray() {
        if (this.tokens) {
            return this.tokens.split(",").map(t => t.trim()).filter(t => t !== '');
        }

        return [];
    }

    removeTokenWithName(name) {
        const tokenArray = this.tokensAsArray();

        for (let i = 0; i < tokenArray.length; i++) {
            if (tokenArray[i] === name) {
                tokenArray.splice(i, 1);
                break;
            }
        }

        this.tokens = tokenArray.join(",");
    }

    parseTextInput(isFinal = false) {
        // Locate tokens in text field
        const textField = this._inputText;
        let text = textField.value;

        const matches = text.match(/((\[|\w+)([\w\-. \[\]_&:%$'#!+=()<>?\/])+,)/);

        if (matches && matches.length > 1) {
            let tokenName = matches[1];
            this.addToken(tokenName);
        }

        // User lost focus
        if (isFinal) {
            // Get latest value
            text = textField.value;
            this.addToken(text);
        }
    }

    addToken(tokenName)
    {
        if (tokenName.length === 0) return;

        const textField = this._inputText;
        textField.value = textField.value.replace(tokenName, '');

        tokenName = tokenName.split(',').join('').trim();

        const maxLength = this.maxTokenLength;
        if (maxLength) tokenName = tokenName.substring(0, maxLength).trim();

        if (tokenName.length === 0) return;

        const tokenArray = this.tokensAsArray();

        // The tag tables' unique keys are case-insensitive, so a repeat in a
        // different case is still a duplicate as far as the database is concerned.
        if (tokenArray.some(t => t.toLowerCase() === tokenName.toLowerCase())) return;

        const li = document.createElement("li");
        li.classList.add("token");
        li.setAttribute("data-val", tokenName);
        li.textContent = tokenName;
        this.shadowRoot.querySelectorAll("ul")[0].insertBefore(li, textField.parentNode);

        const deleteBtn = document.createElement('div');
        deleteBtn.className = 'delete';
        li.appendChild(deleteBtn);

        deleteBtn.addEventListener("click", e => {
            this.deleteToken(deleteBtn);
        });

        tokenArray.push(tokenName);

        this.tokens = tokenArray.join(",");
    }

    deleteToken(obj) {
        let token = obj;

        if (!obj.classList.contains("token") || obj.tagName !== "li") {
            token = obj.closest("li");
        }

        if (!token) {
            console.log("Unable to delete token - could not locate element.");
            return;
        }

        const tokenName = token.textContent;
        this.removeTokenWithName(tokenName);

        // Remove DOM object
        token.remove();
    }

    syncTokens() {
        if (this.name && this._hiddenText) {
            this._hiddenText.value = this.tokens;
        }
    }

    render() {
        // Remove existing tokens
        this.shadowRoot.querySelectorAll('li.token').forEach((item) => {
            item.remove();
        })

        const tokenArray = this.tokensAsArray();

        for (let i = 0; i < tokenArray.length; i++) {
            let token = tokenArray[i];

            if (token !== '') {
                const li = document.createElement('li');
                li.className = 'token';
                li.setAttribute('data-val', token);
                li.textContent = token;

                const deleteBtn = document.createElement('div');
                deleteBtn.className = 'delete';
                li.appendChild(deleteBtn);

                deleteBtn.addEventListener("click", e => {
                    this.deleteToken(deleteBtn);
                });

                this._ul.insertBefore(li, this._li_text);
            }
        }

        this.syncTokens();

        if (!!this.getAttribute('autofocus')) {
            this._inputText.focus();
        }
    }
}

customElements.define('token-input', TokenInput);